import React, { FC } from 'react';
import { getDisplayDate, getDisplayValue } from '@web/utils';
import { DATE_FORMAT } from '@web/config/constants';
import { StyleProp, TextStyle } from 'react-native';
import { TableCell, TableRow } from '@web/components/table';
import { Typography } from '@packages/ui/shared';
import { getLogger } from '@packages/core/logging';
import { createUseStyles } from 'react-jss';
import { CampaignsTableRowModel } from './campaigns-list';
import { usePublicationsDispatch } from '../../stis-publications/context/publications.hooks';
import { openCampaignPublication } from './campaigns-summary-card';
import { BmisBatteryBadge } from '@web/bmis/components/bmis-battery-badge';
import { JobFieldCell } from '../../dms/components/dms-job-cell';
import { Link, useParams } from 'react-router-dom';
import { useAuthState } from '@packages/contexts/auth';

const logger = getLogger('vin-sessions');

interface CampaignsTableRowProps {
    rowData: CampaignsTableRowModel;
}

const useCampaignsListStyles = createUseStyles({
    jobCell: {
        minWidth: '165px',
    },
    jobsCountWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    jobsCountInteractive: {
        cursor: 'pointer',
        '&:hover': {
            opacity: 0.85,
        },
    },
    expandedJobRow: {
        background: '#F8F8F8',
        '& td': {
            background: '#F8F8F8',
        },
    },
    typeCategoryColumn: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    categoryText: {
        color: '#4C525D',
        fontFamily: 'Roboto',
        fontSize: 13,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '16px',
    },
});

export const CampaignsTableRow: FC<CampaignsTableRowProps> = ({ rowData }) => {
    const classes = useCampaignsListStyles();
    const { vin: vinString } = useParams<{ vin: string }>();
    const publicationDispatch = usePublicationsDispatch();
    const { token } = useAuthState();
    const {
        rowId,
        code,
        roNumber,
        programType,
        programCategory,
        description,
        effectiveDate,
        expirationDate,
        programMiles,
        programStatus,
    } = rowData;

    const typographyStyles = {
        jobCell: {
            display: 'block',
            maxWidth: '100%',
            overflow: 'hidden',
            marginTop: 4,
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'middle',
            cursor: 'pointer',
        } as unknown as StyleProp<TextStyle>,
        link: {
            fontSize: 15,
            fontWeight: 400,
            '&:hover': {
                textDecoration: 'underline!important',
            },
        } as unknown as StyleProp<TextStyle>,
        categoryTextStyles: {
            color: '#4C525D',
            fontFamily: 'Roboto',
            fontSize: 13,
            fontWeight: '400',
        } as unknown as StyleProp<TextStyle>,
    };

    return (
        <>
            <TableRow key={rowId}>
                <TableCell>
                    <Typography
                        numberOfLines={1}
                        variant="label"
                        style={typographyStyles.link}
                        onPress={() => openCampaignPublication(code, token, publicationDispatch)}
                    >
                        <Link to={'#'}>{getDisplayValue(code)}</Link>
                    </Typography>
                </TableCell>
                <TableCell>
                    <div className={classes.typeCategoryColumn}>
                        <Typography variant="label">{getDisplayValue(programType)}</Typography>
                        <Typography style={typographyStyles.categoryTextStyles}>
                            {getDisplayValue(programCategory)}
                        </Typography>
                    </div>
                </TableCell>
                <TableCell>
                    <div style={{ display: 'inline-flex', alignItems: 'center', width: 'fit-content' }}>
                        {(() => {
                            const s = String(programStatus || '')
                                .toLowerCase()
                                .trim();
                            let label = s;
                            if (s.startsWith('open')) {
                                if (s.includes('limited')) label = 'open parts limited';
                                else if (s.includes('remedy')) label = 'open no remedy yet';
                                else label = 'open';
                            } else if (s.includes('expired')) {
                                label = 'expired';
                            } else if (s.includes('complete')) {
                                label = 'completed';
                            }
                            return <BmisBatteryBadge isCampaign={true} label={label} />;
                        })()}
                    </div>
                </TableCell>
                <JobFieldCell
                    rowId={rowId}
                    rawValue={getDisplayValue(description)}
                    tooltipPrefix="description"
                    className={classes.jobCell}
                    typographyStyles={typographyStyles.jobCell}
                />
                <JobFieldCell
                    rowId={rowId}
                    rawValue={getDisplayDate(effectiveDate, DATE_FORMAT.MON_DAY_YEAR)}
                    tooltipPrefix="effectiveDate"
                    className={classes.jobCell}
                    typographyStyles={typographyStyles.jobCell}
                />
                <JobFieldCell
                    rowId={rowId}
                    rawValue={getDisplayDate(expirationDate, DATE_FORMAT.MON_DAY_YEAR)}
                    tooltipPrefix="expirationDate"
                    className={classes.jobCell}
                    typographyStyles={typographyStyles.jobCell}
                />
                <JobFieldCell
                    rowId={rowId}
                    rawValue={getDisplayValue(programMiles)}
                    tooltipPrefix="programMiles"
                    className={classes.jobCell}
                    typographyStyles={typographyStyles.jobCell}
                />
                {(() => {
                    const ro = getDisplayValue(roNumber);
                    logger.trace(`Rendering the campaign row RO number [${ro}]`, { roNumber: ro });
                    const normalized = String(ro || '')
                        .toLowerCase()
                        .trim();
                    const hasLink = ro && normalized !== 'n/a';
                    if (hasLink) {
                        return (
                            <TableCell>
                                <Typography numberOfLines={1} variant="label" style={typographyStyles.link}>
                                    <Link to={`/vin/${vinString}/repair-orders/${ro}`}>{ro}</Link>
                                </Typography>
                            </TableCell>
                        );
                    }
                    return (
                        <JobFieldCell
                            rowId={rowId}
                            rawValue={ro}
                            tooltipPrefix="roNumber"
                            className={classes.jobCell}
                            typographyStyles={typographyStyles.jobCell}
                        />
                    );
                })()}
            </TableRow>
        </>
    );
};
